import { TFunction } from 'i18next';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import styles from './exportXlsx.module.scss';
import exportExcelIcon from '../../assets/exportExcel.svg';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FilterContext } from '../../context/FilterProvider';
import ObrasAPI from '../../services/APIObras';
import spinner from '../../assets/loader_export.svg';
import { ReactComponent as error } from '../../assets/error.svg';
import ReactGA from 'react-ga4';
import { AuthContext } from '../../services/authContextProvider';
import { ExportJob, ExportJobStatus } from '../../types/ExportJob';

interface ExportXlsxProps {
  t: TFunction<'translations'>;
  enableExport: boolean;
}

type ExportJobAction = 'cancel' | 'delete';

const HISTORY_PAGE_SIZE = 10;

export const EXPORT_SECTIONS = [
  'generalData',
  'description',
  'location',
  'budget',
  'dates',
  'contract',
  'client',
  'partners',
  'aspects',
  'clasification',
  'bim',
  'esg',
] as const;

type ExportSection = typeof EXPORT_SECTIONS[number];

const EXPORT_SECTION_LABELS: Record<ExportSection, string> = {
  generalData: 'advancedFilters.generalData',
  description: 'advancedFilters.description',
  location: 'advancedFilters.locationEnvironment',
  budget: 'advancedFilters.budget',
  dates: 'advancedFilters.dates',
  contract: 'advancedFilters.contract',
  client: 'advancedFilters.clients',
  partners: 'advancedFilters.partners',
  aspects: 'advancedFilters.technicalAspects',
  clasification: 'advancedFilters.classification',
  bim: 'advancedFilters.bim',
  esg: 'advancedFilters.esg',
};

const HEAVY_EXPORT_SECTION_THRESHOLD = 4;

interface PendingExportJobAction {
  job: ExportJob;
  action: ExportJobAction;
}

const primaryButtonSx = {
  textTransform: 'none',
  backgroundColor: '#FF0000',
  borderColor: '#FF0000',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#DF0202',
    borderColor: '#DF0202',
  },
  '&:active': {
    backgroundColor: '#BC0000',
    borderColor: '#BC0000',
  },
} as const;

const textActionButtonSx = {
  textTransform: 'none',
  color: '#FF0000',
  backgroundColor: 'transparent',
  '&:hover': {
    color: '#DF0202',
    backgroundColor: 'rgba(255, 0, 0, 0.06)',
  },
  '&:active': {
    color: '#BC0000',
  },
} as const;

const secondaryButtonSx = {
  textTransform: 'none',
  color: '#FF0000',
  backgroundColor: 'transparent',
  '&:hover': {
    color: '#DF0202',
    backgroundColor: 'rgba(255, 0, 0, 0.06)',
  },
  '&:active': {
    color: '#BC0000',
  },
} as const;

function ExportXlsx(props: ExportXlsxProps) {
  const { t, enableExport } = props;
  const filterContext = useContext(FilterContext);
  const authContext = useContext(AuthContext);
  const api = ObrasAPI.getInstance();
  const hasGlobalDownloadPermission = authContext.canSystem('can_download')
    || authContext.canSystem('can_manage_user_roles');
  const hasScopedDownloadPermission = authContext.canSystem('can_save_any_project')
    || authContext.canSystem('can_validate_any_project');
  const hasDownloadPermission = hasGlobalDownloadPermission || hasScopedDownloadPermission;
  const [scopedExportAllowed, setScopedExportAllowed] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);
  const [openError, setOpenError] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [pendingAction, setPendingAction] = useState<PendingExportJobAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openSectionSelector, setOpenSectionSelector] = useState(false);
  const [selectedSections, setSelectedSections] = useState<ExportSection[]>(['generalData']);

  const optionalSections = EXPORT_SECTIONS.slice(1);
  const selectedOptionalSectionCount = selectedSections.filter((section) => section !== 'generalData').length;
  const allOptionalSectionsSelected = selectedOptionalSectionCount === optionalSections.length;
  const isHeavyExport = selectedSections.length > HEAVY_EXPORT_SECTION_THRESHOLD;
  const canExport = enableExport && (hasGlobalDownloadPermission || scopedExportAllowed);

  useEffect(() => {
    if (hasGlobalDownloadPermission || !hasScopedDownloadPermission || !enableExport
      || Object.keys(filterContext.appliedFilters).length === 0) {
      setScopedExportAllowed(false);
      setCheckingEligibility(false);
      return;
    }

    let active = true;
    setScopedExportAllowed(false);
    setCheckingEligibility(true);
    const timer = window.setTimeout(() => {
      void api.getObrasXLSXEligibilityAPI(filterContext.transformMasterFilters(filterContext.appliedFilters))
        .then((allowed) => {
          if (!active) return;
          setScopedExportAllowed(allowed);
          setCheckingEligibility(false);
        })
        .catch(() => {
          if (!active) return;
          setScopedExportAllowed(false);
          setCheckingEligibility(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, enableExport, filterContext.appliedFilters, filterContext.masterFilters,
    hasGlobalDownloadPermission, hasScopedDownloadPermission]);

  const refreshJobs = useCallback(async (page = 0) => {
    try {
      const currentJobs = await api.getObrasXLSXJobsAPI(page, HISTORY_PAGE_SIZE);
      setJobs(currentJobs.results);
      setHistoryPage(currentJobs.page);
      setHistoryTotalPages(currentJobs.totalPages);
      setHistoryTotalElements(currentJobs.totalElements);
    } catch (refreshError) {
      console.debug('Unable to refresh export history', refreshError);
    }
  }, [api]);

  useEffect(() => {
    if (!hasDownloadPermission) return;

    void refreshJobs(historyPage);
    const timer = window.setInterval(() => void refreshJobs(historyPage), 5000);
    return () => window.clearInterval(timer);
  }, [hasDownloadPermission, historyPage, refreshJobs]);

  useEffect(() => {
    if (historyPage > 0 && (historyTotalPages === 0 || historyPage >= historyTotalPages)) {
      setHistoryPage(Math.max(historyTotalPages - 1, 0));
    }
  }, [historyPage, historyTotalPages]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const showExportError = (permission: boolean) => {
    setHasPermission(permission);
    setOpenError(true);
    setExporting(false);
  };

  const requestJobAction = (job: ExportJob, action: ExportJobAction) => {
    setActionError(null);
    setPendingAction({ job, action });
  };

  const closeActionDialog = () => {
    if (actionLoading) return;
    setActionError(null);
    setPendingAction(null);
  };

  const confirmJobAction = async () => {
    if (!pendingAction || actionLoading) return;

    setActionLoading(true);
    setActionError(null);
    try {
      if (pendingAction.action === 'cancel') {
        await api.cancelObrasXLSXJobAPI(pendingAction.job.id);
      } else {
        await api.deleteObrasXLSXJobAPI(pendingAction.job.id);
      }
      await refreshJobs();
      setPendingAction(null);
    } catch (error) {
      console.log(error);
      const response = (error as { response?: { status?: number } }).response;
      setActionError(response?.status === 409
        ? String(t('exportExcel.actionConflict'))
        : String(t('exportExcel.actionError')));
      await refreshJobs();
    } finally {
      setActionLoading(false);
    }
  };

  const downloadJob = async (job: ExportJob) => {
    try {
      const download = await api.getObrasXLSXJobDownloadAPI(job.id);
      const isLocalDownload = download.url.startsWith('/');
      const objectUrl = isLocalDownload
        ? window.URL.createObjectURL(await api.getObrasXLSXJobFileAPI(download.url))
        : null;
      const link = document.createElement('a');
      link.href = objectUrl || download.url;
      link.setAttribute('download', download.fileName);
      if (!isLocalDownload) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      console.log(downloadError);
      showExportError(true);
    }
  };

  const openExportSectionSelector = () => {
    if (!canExport) {
      ReactGA.event('download', {
        event_category: 'excel',
        capability: 'can_download',
        hasPermissions: false,
        numFilters: Object.keys(filterContext.appliedFilters).length,
      });
      showExportError(false);
    } else if (filterContext.getAppliedFiltersCount() === 0 && filterContext.getAppliedAdvancedFiltersCount() === 0) {
      ReactGA.event('download', {
        event_category: 'excel',
        capability: 'can_download',
        hasPermissions: true,
        numFilters: 0,
      });
      showExportError(true);
    } else {
      setOpenSectionSelector(true);
    }
  };

  const closeSectionSelector = () => {
    if (!exporting) setOpenSectionSelector(false);
  };

  const toggleSection = (section: ExportSection) => {
    if (section === 'generalData') return;
    setSelectedSections((currentSections) => currentSections.includes(section)
      ? currentSections.filter((currentSection) => currentSection !== section)
      : [...currentSections, section]);
  };

  const toggleAllSections = () => {
    setSelectedSections(allOptionalSectionsSelected ? ['generalData'] : [...EXPORT_SECTIONS]);
  };

  const downloadCSV = async () => {
    try {
      setExporting(true);
      ReactGA.event('download', {
        event_category: 'excel',
        capability: 'can_download',
        hasPermissions: true,
        numFilters: Object.keys(filterContext.appliedFilters).length,
      });
      await api.createObrasXLSXJobAPI(
        filterContext.transformMasterFilters(filterContext.appliedFilters),
        selectedSections,
      );
      setHistoryPage(0);
      await refreshJobs(0);
      setOpenHistory(true);
      setHasPermission(true);
      setExporting(false);
    } catch (exportError) {
      console.log(exportError);
      const response = (exportError as { response?: { status?: number } }).response;
      if (response?.status === 403) {
        showExportError(false);
      } else {
        showExportError(true);
      }
    }
  };

  const statusLabel = (status: ExportJobStatus): string =>
    t(`exportExcel.${status === 'PENDING' ? 'queued' : status.toLowerCase()}`);

  const formatDate = (date: string | null): string => {
    if (!date) return '-';
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(date));
  };

  const getProgressPercentage = (job: ExportJob): number | null => {
    if (job.status === 'COMPLETED') return 100;
    if (job.status === 'FAILED' || job.status === 'CANCELLED' || job.total <= 0) return null;
    return Math.min(100, Math.max(0, Math.round((job.processed / job.total) * 100)));
  };

  const isExpired = (job: ExportJob): boolean => {
    if (job.status !== 'COMPLETED') return false;
    if (!job.expiresAt) return true;
    const expirationTime = new Date(job.expiresAt).getTime();
    return !Number.isFinite(expirationTime) || expirationTime <= now;
  };

  const formatRemainingTime = (seconds: number): string => {
    const roundedSeconds = Math.max(1, Math.ceil(seconds));
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const remainingSeconds = roundedSeconds % 60;

    if (hours > 0) return `${hours} h${minutes > 0 ? ` ${minutes} min` : ''}`;
    if (minutes > 0) return `${minutes} min`;
    return `${remainingSeconds} s`;
  };

  const getEstimatedTime = (job: ExportJob): string => {
    if (job.status === 'COMPLETED') return t('exportExcel.completedTime');
    if (job.status === 'FAILED') return '-';
    if (job.status === 'CANCELLED') return t('exportExcel.cancelledTime');
    if (job.status === 'PENDING') return t('exportExcel.queued');
    if (job.total > 0 && job.processed >= job.total) return t('exportExcel.preparingZip');
    if (!job.startedAt || job.processed <= 0 || job.total <= 0) {
      return t('exportExcel.calculatingTime');
    }

    const startedAt = new Date(job.startedAt).getTime();
    if (!Number.isFinite(startedAt)) return t('exportExcel.calculatingTime');

    const elapsedSeconds = Math.max((now - startedAt) / 1000, 1);
    const projectsPerSecond = job.processed / elapsedSeconds;
    const remainingProjects = Math.max(job.total - job.processed, 0);
    const remainingSeconds = remainingProjects / projectsPerSecond;

    if (!Number.isFinite(remainingSeconds)) return t('exportExcel.calculatingTime');
    return t('exportExcel.remaining', { time: formatRemainingTime(remainingSeconds) });
  };

  if (!hasDownloadPermission) return null;

  return (
    <div data-testid='exportXlsx-test' className={styles.exportContainer}>
      <Stack direction="row" spacing={1} alignItems="center">
        {exporting ?
          <Button
            variant="text"
            color="error"
            disabled={true}
            className={styles.buttonDisable}
            sx={{ textTransform: 'none' }}
            startIcon={<img src={spinner} alt="" />}
          >
            {t('exportExcel.textButton')}
          </Button>
          :
          <Button
            variant="text"
            onClick={openExportSectionSelector}
            className={canExport ? styles.buttonEnable : styles.buttonDisableExport}
            disabled={!canExport}
            sx={{ textTransform: 'none' }}
            startIcon={<img src={exportExcelIcon} alt="" />}
          >
            {t('exportExcel.textButton')}
          </Button>
        }
        {checkingEligibility && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            role="status"
            aria-live="polite"
            className={styles.eligibilityStatus}
          >
            <CircularProgress size={16} aria-hidden="true" />
            <span>{t('exportExcel.checkingEligibility')}</span>
          </Stack>
        )}
        <Button
          variant="text"
          onClick={() => setOpenHistory(true)}
          disabled={historyTotalElements === 0}
          className={historyTotalElements === 0 ? styles.buttonDisableExport : styles.historyButton}
          sx={{ textTransform: 'none' }}
        >
          {t('exportExcel.history')}
        </Button>
      </Stack>
      <Dialog
        open={openSectionSelector}
        onClose={closeSectionSelector}
        fullWidth
        maxWidth="sm"
        classes={{ paper: styles.sectionDialog }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle>{t('exportExcel.selectSectionsTitle')}</DialogTitle>
        <DialogContent className={styles.sectionDialogContent}>
          <div className={styles.sectionDialogDescription}>
            {t('exportExcel.selectSectionsDescription')}
          </div>
          <FormGroup className={styles.sectionList}>
            {EXPORT_SECTIONS.map((section) => (
              <FormControlLabel
                key={section}
                control={
                  <Checkbox
                    checked={selectedSections.includes(section)}
                    disabled={section === 'generalData'}
                    onChange={() => toggleSection(section)}
                    inputProps={{ 'aria-label': String(t(EXPORT_SECTION_LABELS[section])) }}
                  />
                }
                label={t(EXPORT_SECTION_LABELS[section])}
              />
            ))}
          </FormGroup>
          <FormControlLabel
            className={styles.selectAllSections}
            control={
              <Checkbox
                checked={allOptionalSectionsSelected}
                indeterminate={selectedOptionalSectionCount > 0 && !allOptionalSectionsSelected}
                onChange={toggleAllSections}
                inputProps={{ 'aria-label': String(t('exportExcel.selectAllSections')) }}
              />
            }
            label={t(allOptionalSectionsSelected
              ? 'exportExcel.deselectAllSections'
              : 'exportExcel.selectAllSections')}
          />
          {isHeavyExport && (
            <Alert severity="warning" className={styles.heavyExportWarning}>
              {t('exportExcel.heavyExportWarning', { count: selectedSections.length })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions className={styles.sectionDialogActions}>
          <Button
            className={styles.confirmationCancelButton}
            onClick={closeSectionSelector}
            sx={secondaryButtonSx}
          >
            {t('exportExcel.cancelSelection')}
          </Button>
          <Button
            className={styles.buttonConfirm}
            onClick={() => {
              setOpenSectionSelector(false);
              void downloadCSV();
            }}
            disableRipple={true}
            variant="contained"
            sx={primaryButtonSx}
          >
            {t('exportExcel.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        maxWidth={false}
        fullWidth
        classes={{ paper: styles.dialogHistory }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
        PaperProps={{ sx: { borderRadius: '20px' } }}
        open={openHistory}
        onClose={() => setOpenHistory(false)}
      >
        <DialogTitle>{t('exportExcel.history')}</DialogTitle>
        <DialogContent className={styles.historyDialogContent}>
          {jobs.length === 0 ?
            <div>{t('exportExcel.emptyHistory')}</div>
            :
            <TableContainer component={Paper} elevation={0} className={styles.historyTableContainer}>
              <Table size="small" aria-label={t('exportExcel.history')} className={styles.historyTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.createdAtCell}>{t('exportExcel.createdAt')}</TableCell>
                    <TableCell className={styles.startedAtCell}>{t('exportExcel.startedAt')}</TableCell>
                    <TableCell className={styles.finishedAtCell}>{t('exportExcel.finishedAt')}</TableCell>
                    <TableCell className={styles.statusCell}>{t('exportExcel.status')}</TableCell>
                    <TableCell className={styles.progressCell}>{t('exportExcel.progress')}</TableCell>
                    <TableCell className={styles.estimatedCell}>{t('exportExcel.estimatedTime')}</TableCell>
                    <TableCell className={styles.fileCell}>{t('exportExcel.file')}</TableCell>
                    <TableCell className={styles.actionsCell} align="right">{t('exportExcel.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => {
                    const isFailed = job.status === 'FAILED';
                    const isCancelled = job.status === 'CANCELLED';
                    const expired = isExpired(job);
                    const percentage = getProgressPercentage(job);
                    const hasProgress = !isFailed && !isCancelled && percentage !== null;
                    const isCalculating = !isFailed && !isCancelled && percentage === null && job.status === 'PROCESSING';
                    const canCancel = job.status === 'PENDING' || job.status === 'PROCESSING';
                    const canDelete = job.status === 'COMPLETED' || isFailed || isCancelled;
                    const isCurrentActionLoading = actionLoading && pendingAction?.job.id === job.id;

                    return (
                      <React.Fragment key={job.id}>
                        <TableRow>
                          <TableCell className={`${styles.historyTableCell} ${styles.createdAtCell}`}>{formatDate(job.createdAt)}</TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.startedAtCell}`}>{formatDate(job.startedAt)}</TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.finishedAtCell}`}>{formatDate(job.finishedAt)}</TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.statusCell}`}>
                            <span className={expired
                              ? styles.statusBadgeExpired
                              : isFailed ? styles.statusBadgeFailed
                                : isCancelled ? styles.statusBadgeCancelled : styles.statusBadge}>
                              {expired ? t('exportExcel.expired') : statusLabel(job.status)}
                            </span>
                          </TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.progressCell}`}>
                            <div>
                              {hasProgress ?
                                <LinearProgress
                                  className={styles.progressBar}
                                  variant="determinate"
                                  value={percentage ?? 0}
                                />
                                : isCalculating ?
                                  <LinearProgress className={styles.progressBar} />
                                  : null}
                              <span className={styles.progressText}>
                                {hasProgress ? `${percentage}%` : isCalculating ? t('exportExcel.calculating') : isFailed ? t('exportExcel.notAvailable') : '-'}
                              </span>
                              <span className={styles.progressCount}>
                                {job.total > 0 ? `${job.processed}/${job.total}` : '-'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.estimatedCell}`}>{getEstimatedTime(job)}</TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.fileCell}`} title={job.fileName || undefined}>{job.fileName || '-'}</TableCell>
                          <TableCell className={`${styles.historyTableCell} ${styles.actionsCell}`} align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {job.status === 'COMPLETED' && !expired && (
                                <Button
                                  className={styles.actionButton}
                                  size="small"
                                  disabled={isCurrentActionLoading}
                                  onClick={() => downloadJob(job)}
                                  sx={textActionButtonSx}
                                >
                                  {t('exportExcel.download')}
                                </Button>
                              )}
                              {canCancel && (
                                <Button
                                  className={`${styles.actionButton} ${styles.cancelButton}`}
                                  size="small"
                                  disabled={actionLoading}
                                  onClick={() => requestJobAction(job, 'cancel')}
                                  sx={textActionButtonSx}
                                >
                                  {t('exportExcel.cancel')}
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  className={`${styles.actionButton} ${styles.deleteButton}`}
                                  size="small"
                                  disabled={actionLoading}
                                  onClick={() => requestJobAction(job, 'delete')}
                                  sx={textActionButtonSx}
                                >
                                  {t('exportExcel.delete')}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {isFailed && (
                          <TableRow className={styles.failureRow}>
                            <TableCell className={styles.failureCell} colSpan={8}>
                              <div className={styles.failureMessage} title={job.error || String(t('exportExcel.unknownError'))}>
                                <span className={styles.failureLabel}>{t('exportExcel.failureReason')}:</span>
                                <span className={styles.failureText}>{job.error || t('exportExcel.unknownError')}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          }
          {historyTotalPages > 1 && (
            <Pagination
              className={styles.historyPagination}
              count={historyTotalPages}
              page={historyPage + 1}
              onChange={(_, page) => setHistoryPage(page - 1)}
              aria-label={String(t('exportExcel.history'))}
            />
          )}
          <Stack spacing={2} direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Button
              className={styles.buttonConfirm}
              onClick={() => setOpenHistory(false)}
              disableRipple={true}
              variant="contained"
              sx={primaryButtonSx}
            >
              {t('exportExcel.closeHistory')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        open={pendingAction !== null}
        onClose={closeActionDialog}
        PaperProps={{ sx: { borderRadius: '20px', minWidth: '420px', maxWidth: '520px' } }}
      >
        <DialogTitle>
          {pendingAction?.action === 'cancel'
            ? t('exportExcel.cancelExportTitle')
            : t('exportExcel.deleteExportTitle')}
        </DialogTitle>
        <DialogContent>
          <div className={styles.confirmationText}>
            {pendingAction?.action === 'cancel'
              ? t('exportExcel.cancelExportDescription')
              : t('exportExcel.deleteExportDescription')}
          </div>
          {actionError && <div className={styles.actionError}>{actionError}</div>}
          <Stack spacing={1} direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              className={styles.confirmationCancelButton}
              onClick={closeActionDialog}
              disabled={actionLoading}
              sx={secondaryButtonSx}
            >
              {t('exportExcel.cancelDialog')}
            </Button>
            <Button
              className={styles.buttonConfirm}
              onClick={() => void confirmJobAction()}
              disabled={actionLoading}
              disableRipple={true}
              variant="contained"
              sx={primaryButtonSx}
            >
              {actionLoading ? t('exportExcel.processingAction') : t('exportExcel.confirmAction')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        classes={{ paper: styles.dialogError }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
        PaperProps={{ sx: { borderRadius: '20px', minWidth: '700px' } }}
        open={openError}
        onClose={() => setOpenError(false)}
      >
        <div className={styles.dialogContentError}>
          <div data-testid='error-test' className={styles.serverError}>
            {!hasPermission ?
              <div className={styles.defaultError}>
                <div id='error-title' className={styles.title}>{t('exportExcel.needPermissions')}</div>
                <div id='error-title2' className={styles.title2}>{t('exportExcel.serviceNow')}</div>
              </div>
              :
              filterContext.getAppliedFiltersCount() === 0 && filterContext.getAppliedAdvancedFiltersCount() === 0 ?
                <div className={styles.defaultError}>
                  <div id='error-title' className={styles.title}>{t('exportExcel.errorFilterTitle')}</div>
                  <div id='error-subtitle' className={styles.subtitle}>{t('exportExcel.errorFilterDescription')}</div>
                </div>
                :
                <div className={styles.serverErrorContent}>
                  <SvgIcon component={error} inheritViewBox />
                  <div id='error-title' className={styles.title}>{t('exportExcel.titleErrorExport')}</div>
                  <div id='error-subtitle' className={styles.subtitle}>{t('exportExcel.textErrorExport')}</div>
                </div>
            }
          </div>
          <Stack spacing={2} direction="row" justifyContent="center">
            <Button className={styles.buttonConfirm} onClick={() => setOpenError(false)} disableRipple={true} variant="contained" sx={primaryButtonSx}>
              {t('exportExcel.acceptButton')}
            </Button>
          </Stack>
        </div>
      </Dialog>
    </div>
  );
}

export default withTranslation()(ExportXlsx);
